import { useState, type FormEvent } from 'react';
import {
  useHouseholdMembers,
  useCreateHousehold,
  useInviteMember,
  useUpdateMember,
  useCreateSharedMember,
  useUpdateSharedMember,
  useDeleteSharedMember,
  useHouseholdSettings,
} from '../hooks/useHousehold';
import type { HouseholdMember } from '@chore-project/shared';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import { useActiveMemberContext } from '../context/ActiveMemberContext';
import { useChoreAssignments, useTodayMemberStats } from '../hooks/useChoreAssignments';
import { useDailyStats, useCurrentWeekDailyStats } from '../hooks/useUserPoints';
import { AssignmentStatus } from '@chore-project/shared';
import { Modal } from '../components/Modal';
import { MemberAvatar } from '../components/MemberAvatar';
import { HouseholdSwitcher } from '../components/HouseholdSwitcher';
import { EmptyState } from '../components/EmptyState';
import { Switch } from '../components/Switch';
import { useAuth } from '../hooks/useAuth';

const AVATAR_COLORS = [
  'oklch(65% 0.2 25)',   // coral
  'oklch(72% 0.17 65)',  // amber
  'oklch(62% 0.14 145)', // sage
  'oklch(60% 0.13 188)', // teal
  'oklch(62% 0.16 240)', // sky
  'oklch(52% 0.18 275)', // indigo
  'oklch(57% 0.2 310)',  // violet
  'oklch(62% 0.18 350)', // rose
] as const;

const AVATAR_ICONS = [
  'person', 'star', 'heart', 'leaf', 'flame',
  'musical-notes', 'bicycle', 'cafe', 'planet', 'diamond',
  'paw', 'game-controller', 'book', 'camera', 'pizza',
  'rocket', 'barbell', 'headset', 'color-palette', 'shield',
  'compass', 'football', 'skull', 'glasses',
] as const;


export function HouseholdPage() {
  const { user } = useAuth();
  const { householdId, setSelectedId, households, isLoading } = useSelectedHousehold();
  const { activeMemberId } = useActiveMemberContext();
  // Acting as a shared-device user is a restricted, view-leaning mode: no creating
  // households, no adding members, no editing settings.
  const actingAsShared = !!activeMemberId;
  const [showAdd, setShowAdd] = useState(false);
  const [showWhatMeans, setShowWhatMeans] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showShareDevice, setShowShareDevice] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<HouseholdMember | null>(null);

  const [newName, setNewName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Member personalization state
  const [editMemberName, setEditMemberName] = useState('');
  const [editAvatarColor, setEditAvatarColor] = useState<typeof AVATAR_COLORS[number]>(AVATAR_COLORS[0]);
  const [editAvatarIcon, setEditAvatarIcon] = useState<typeof AVATAR_ICONS[number]>(AVATAR_ICONS[0]);

  // Shared-device user creation state
  const [sharedName, setSharedName] = useState('');
  const [sharedColor, setSharedColor] = useState<typeof AVATAR_COLORS[number]>(AVATAR_COLORS[1]);
  const [sharedIcon, setSharedIcon] = useState<typeof AVATAR_ICONS[number]>(AVATAR_ICONS[0]);
  const [sharedInPool, setSharedInPool] = useState(true);

  const { data: members } = useHouseholdMembers(householdId);
  const { data: todayStats } = useTodayMemberStats(householdId);
  const { data: settings } = useHouseholdSettings(householdId);
  const createHousehold = useCreateHousehold();
  const inviteMember = useInviteMember(householdId);
  const updateMember = useUpdateMember(householdId);
  const createSharedMember = useCreateSharedMember(householdId);
  const updateSharedMember = useUpdateSharedMember(householdId);
  const deleteSharedMember = useDeleteSharedMember(householdId);

  const today = new Date().toISOString().split('T')[0] ?? '';
  const { data: todayDailyStats } = useDailyStats(householdId, today);
  const { days: weekDays, data: weekStats } = useCurrentWeekDailyStats(householdId);
  const { data: allAssignments } = useChoreAssignments(householdId);

  // Hero of the Day: member with highest dailyPoints today
  const heroStat = (todayDailyStats ?? []).reduce<{ userId: string; dailyPoints: number } | null>(
    (best, s) => (!best || s.dailyPoints > best.dailyPoints ? s : best),
    null,
  );
  const heroMember = heroStat
    ? (members ?? []).find((m) => m.userId === heroStat.userId)
    : null;

  // Weekly leaderboard: points from DailyStats, completed/missed from assignments
  const weekStart = weekDays[0] ?? '';
  const weekEnd = weekDays[weekDays.length - 1] ?? '';

  const weeklyPoints = new Map<string, number>();
  for (const stat of weekStats) {
    weeklyPoints.set(stat.userId, (weeklyPoints.get(stat.userId) ?? 0) + stat.dailyPoints);
  }
  const weekCompleted = new Map<string, number>();
  const weekMissed = new Map<string, number>();
  for (const a of allAssignments ?? []) {
    if (a.dueDate >= weekStart && a.dueDate <= weekEnd) {
      if (a.status === AssignmentStatus.COMPLETED) {
        weekCompleted.set(a.assigneeId, (weekCompleted.get(a.assigneeId) ?? 0) + 1);
      } else if (a.status === AssignmentStatus.MISSED) {
        weekMissed.set(a.assigneeId, (weekMissed.get(a.assigneeId) ?? 0) + 1);
      }
    }
  }
  const leaderboardRows = (members ?? [])
    .map((m) => ({
      member: m,
      points: weeklyPoints.get(m.userId) ?? 0,
      completed: weekCompleted.get(m.userId) ?? 0,
      missed: weekMissed.get(m.userId) ?? 0,
    }))
    .sort((a, b) => b.points - a.points);

  const handleCreateHousehold = async (e: FormEvent) => {
    e.preventDefault();
    const h = await createHousehold.mutateAsync({ name: newName });
    setSelectedId(h.householdId);
    setNewName('');
    setShowCreate(false);
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    const result = await inviteMember.mutateAsync({ email: inviteEmail });
    setInviteToken(result.token);
    setInviteEmail('');
  };

  const closeInvite = () => {
    setShowInvite(false);
    setInviteToken(null);
    setInviteEmail('');
    setLinkCopied(false);
  };

  const getInviteUrl = () =>
    `${window.location.origin}/invitations/${inviteToken}`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(getInviteUrl());
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const canShare = __NATIVE__ || typeof navigator.share === 'function';

  const handleShareLink = async () => {
    if (__NATIVE__) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: 'Join my household on Chortle', url: getInviteUrl() });
    } else {
      await navigator.share({ title: 'Join my household on Chortle', url: getInviteUrl() });
    }
  };

  const openEditMember = () => {
    const me = (members ?? []).find((m) => m.userId === user?.sub);
    setEditMemberName(me?.memberName || me?.email?.split('@')[0] || '');
    setEditAvatarColor((me?.avatarColor as typeof AVATAR_COLORS[number]) ?? AVATAR_COLORS[0]);
    setEditAvatarIcon((me?.avatarIcon as typeof AVATAR_ICONS[number]) ?? AVATAR_ICONS[0]);
    setShowEditMember(true);
  };

  const handleSaveMember = async (e: FormEvent) => {
    e.preventDefault();
    const name = editMemberName.trim();
    if (!name) return;
    await updateMember.mutateAsync({ memberName: name, avatarColor: editAvatarColor, avatarIcon: editAvatarIcon });
    setShowEditMember(false);
  };

  const openShareDevice = () => {
    setSharedName('');
    setSharedColor(AVATAR_COLORS[1]);
    setSharedIcon(AVATAR_ICONS[0]);
    setSharedInPool(true);
    setShowAdd(false);
    setShowShareDevice(true);
  };

  const handleCreateSharedMember = async (e: FormEvent) => {
    e.preventDefault();
    const name = sharedName.trim();
    if (!name) return;
    await createSharedMember.mutateAsync({
      nickname: name,
      avatarColor: sharedColor,
      avatarIcon: sharedIcon,
      sharedPool: sharedInPool,
    });
    setShowShareDevice(false);
  };

  const handleToggleSharedPool = (m: HouseholdMember) => {
    updateSharedMember.mutate({ userId: m.userId, sharedPool: m.sharedPool === false });
  };

  const handleDeleteSharedMember = async () => {
    if (!deleteTarget) return;
    await deleteSharedMember.mutateAsync({ userId: deleteTarget.userId });
    setDeleteTarget(null);
  };

  // Can the current user manage this shared user (delete, or toggle pool membership)?
  // Its creator or the household owner — and never while acting as a shared user
  // (members can't be managed in that mode).
  const canManageShared = (m: HouseholdMember) =>
    !actingAsShared &&
    m.isShared === true &&
    (m.managedBy === user?.sub || currentHousehold?.ownerUserId === user?.sub);

  if (isLoading) {
    return (
      <div className="page">
        <p className="loading-note">Loading…</p>
      </div>
    );
  }

  const currentHousehold = households?.find((h) => h.householdId === householdId);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Household</h1>
        <div className="page-header-actions">
          <HouseholdSwitcher />
          {!actingAsShared && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowCreate(true)}>
              + New Household
            </button>
          )}
        </div>
      </div>

      {!currentHousehold ? (
        <EmptyState
          icon="home-outline"
          title="No household yet"
          action={
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              Create one
            </button>
          }
        >
          Chortle starts with a household — create one, or join your family&apos;s.
        </EmptyState>
      ) : (
        <>
          {/* Household banner + Hero of the Day */}
          <div className="hero-card household-hero">
            <div className="household-hero-top">
              <div style={{ minWidth: 0 }}>
                <span className="hero-eyebrow">Household</span>
                <h2 className="household-hero-name">{currentHousehold.name}</h2>
              </div>
              {!actingAsShared && (
                <button className="btn-hero-action" onClick={() => setShowAdd(true)}>
                  <ion-icon name="person-add" />
                  <span>Add</span>
                </button>
              )}
            </div>

            <div className="household-hero-spotlight">
              {heroMember && heroStat && heroStat.dailyPoints > 0 ? (
                <>
                  <div className="hero-spotlight-avatar">
                    <MemberAvatar member={heroMember} size="3.25rem" fontSize="1.3rem" />
                    <span className="hero-spotlight-emblem">👑</span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <span className="hero-eyebrow">Hero today</span>
                    <div className="hero-spotlight-name">{heroMember.memberName || heroMember.email || 'Housemate'}</div>
                    <div className="hero-spotlight-sub">{heroStat.dailyPoints} pts today · leading the pack</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="hero-spotlight-crown">
                    <ion-icon name="trophy-outline" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <span className="hero-eyebrow">Hero today</span>
                    <div className="hero-spotlight-name">No hero yet</div>
                    <div className="hero-spotlight-sub">First to finish a chore today takes the crown 👑</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Weekly Leaderboard */}
          {leaderboardRows.length > 1 && (
            <section>
              <p className="section-label">Weekly Leaderboard</p>
              <div className="leaderboard">
                {leaderboardRows.map((row, i) => {
                  const isLeader = i === 0 && row.points > 0;
                  const name = row.member.memberName || row.member.email || 'Housemate';
                  return (
                    <div
                      key={row.member.userId}
                      className={`leaderboard-row${isLeader ? ' leaderboard-row--leader' : ''}`}
                    >
                      <span className="leaderboard-rank">
                        {isLeader ? <ion-icon name="trophy" /> : i + 1}
                      </span>
                      <MemberAvatar member={row.member} size="2.25rem" fontSize="0.9rem" />
                      <div className="leaderboard-name">
                        <span className="leaderboard-name-text">{name}</span>
                        <span className="leaderboard-meta">
                          {row.completed} done{row.missed > 0 ? ` · ${row.missed} missed` : ''}
                        </span>
                      </div>
                      <span className="leaderboard-pts">
                        {row.points}<small>pts</small>
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Members */}
          <section>
            <p className="section-label">
              Members{members && members.length > 0 ? ` · ${members.length}` : ''}
            </p>
            {(members ?? []).length === 0 ? (
              <div className="card">
                <p className="modal-note">No members yet.</p>
              </div>
            ) : (
              <div className="card card--list">
                <ul className="member-list">
                  {members?.map((m) => {
                    const isMe = m.userId === user?.sub;
                    const name = m.memberName || m.email || 'Housemate';
                    const stats = todayStats?.[m.userId];
                    const allDone = stats && stats.pending === 0 && stats.overdue === 0 && stats.completed > 0;
                    return (
                      <li key={m.userId} className="member-item">
                        <MemberAvatar member={m} />
                        <div className="member-info">
                          <div className="member-name-row">
                            <div className="member-name">{name}</div>
                            {m.isShared && <span className="badge badge-neutral">Shared</span>}
                            {isMe && (
                              <button
                                className={`btn btn-ghost btn-sm member-edit-btn${!m.avatarColor ? ' btn-glow' : ''}`}
                                onClick={openEditMember}
                                aria-label="Personalise your member profile"
                              >
                                <ion-icon name="create-outline" />
                              </button>
                            )}
                          </div>
                          {m.email && m.memberName && m.email !== m.memberName && (
                            <div className="member-email">{m.email}</div>
                          )}
                          {stats && (
                            <div className="member-stats">
                              {allDone ? (
                                <span className="badge badge-completed">✓ all done</span>
                              ) : stats.overdue > 0 ? (
                                <span className="badge badge-overdue">{stats.overdue} overdue</span>
                              ) : stats.pending > 0 ? (
                                <span className="member-todo">
                                  {stats.completed > 0
                                    ? `${stats.completed}/${stats.completed + stats.pending} done`
                                    : `${stats.pending} to do`}
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                        {canManageShared(m) && (
                          <div className="member-manage">
                            <button
                              className={`btn btn-sm member-pool-btn ${m.sharedPool === false ? 'btn-ghost' : 'btn-primary'}`}
                              onClick={() => handleToggleSharedPool(m)}
                              disabled={updateSharedMember.isPending}
                              aria-pressed={m.sharedPool !== false}
                              title={
                                m.sharedPool === false
                                  ? `${name} only gets chores meant for them — tap to add them to the shared pool`
                                  : `${name} is in the shared chore pool — tap to give them only their own chores`
                              }
                            >
                              <ion-icon name={m.sharedPool === false ? 'person-outline' : 'people-outline'} />
                              {m.sharedPool === false ? 'Own only' : 'In pool'}
                            </button>
                            <button
                              className="btn btn-ghost btn-sm member-remove-btn"
                              onClick={() => setDeleteTarget(m)}
                              aria-label={`Remove ${name}`}
                              title="Remove shared user"
                            >
                              <ion-icon name="trash-outline" />
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        </>
      )}

      {/* Add member chooser */}
      {showAdd && (
        <Modal title="Add someone" onClose={() => setShowAdd(false)}>
          <div className="sheet-stack">
            <button className="add-choice" onClick={() => { setShowAdd(false); setShowInvite(true); }}>
              <span className="add-choice-icon"><ion-icon name="mail-outline" /></span>
              <span className="sheet-row-text">
                <span className="add-choice-title">Invite by email</span>
                <span className="add-choice-sub">Send a link so they join with their own login</span>
              </span>
              <ion-icon name="chevron-forward" className="sheet-row-chevron" />
            </button>
            <button className="add-choice" onClick={openShareDevice}>
              <span className="add-choice-icon"><ion-icon name="people-outline" /></span>
              <span className="sheet-row-text">
                <span className="add-choice-title">Share this device</span>
                <span className="add-choice-sub">Add someone with no phone — a kid or housemate</span>
              </span>
              <ion-icon name="chevron-forward" className="sheet-row-chevron" />
            </button>
            <button
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: 'center' }}
              onClick={() => setShowWhatMeans(true)}
            >
              <ion-icon name="help-circle-outline" style={{ marginRight: '0.3rem' }} />
              What does this mean?
            </button>
          </div>
        </Modal>
      )}

      {/* What does "share this device" mean? */}
      {showWhatMeans && (
        <Modal title="Sharing a device" onClose={() => setShowWhatMeans(false)}>
          <div className="modal-prose">
            <p>
              Some people in your household might not have their own phone — like a child,
              or a housemate who&apos;d rather not install the app.
            </p>
            <p>
              A <strong>shared-device user</strong> gives them a profile with a nickname and avatar,
              right here on your device. They get their own chores, points and streaks — no email or
              login needed.
            </p>
            <p>
              Switch between everyone from the avatar at the top of the dashboard. While
              you&apos;re acting as a shared user the app stays in their household and is
              view-leaning: you can&apos;t create households, add members or change settings —
              switch back to yourself for that.
            </p>
            <p>
              An <strong>Invite by email</strong> is different: it sends a link so someone joins
              with their own account on their own phone.
            </p>
          </div>
          <button className="btn btn-primary btn-full" style={{ marginTop: '1rem' }} onClick={() => setShowWhatMeans(false)}>
            Got it
          </button>
        </Modal>
      )}

      {/* Share-this-device: create a shared user */}
      {showShareDevice && (
        <Modal title="Add a shared-device user" onClose={() => setShowShareDevice(false)}>
          <form onSubmit={(e) => void handleCreateSharedMember(e)}>
            <div className="field">
              <label htmlFor="shared-name">Nickname</label>
              <input
                id="shared-name"
                type="text"
                value={sharedName}
                onChange={(e) => setSharedName(e.target.value)}
                placeholder="e.g. Mia"
                maxLength={50}
                required
                autoFocus
              />
            </div>

            <div className="field">
              <label>Colour</label>
              <div className="swatch-row">
                {AVATAR_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`swatch${sharedColor === color ? ' swatch--active' : ''}`}
                    onClick={() => setSharedColor(color)}
                    style={{ background: color }}
                    aria-label={color}
                    aria-pressed={sharedColor === color}
                  />
                ))}
              </div>
            </div>

            <div className="field">
              <label>Icon</label>
              <div className="swatch-row">
                {AVATAR_ICONS.map((icon) => {
                  const active = sharedIcon === icon;
                  return (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-swatch${active ? ' icon-swatch--active' : ''}`}
                      onClick={() => setSharedIcon(icon)}
                      style={active ? { background: sharedColor, borderColor: sharedColor } : undefined}
                      aria-label={icon}
                      aria-pressed={active}
                    >
                      <ion-icon name={icon} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="field">
              <div className="item-row setting-row">
                <div className="setting-text">
                  <label>Include in the shared chore pool</label>
                  <div className="setting-desc">
                    When on, unassigned chores can be shared out to them. When off (e.g. for a
                    young child) they only get chores meant specifically for them.
                  </div>
                </div>
                <Switch
                  checked={sharedInPool}
                  onChange={() => setSharedInPool((v) => !v)}
                  label="Include in the shared chore pool"
                />
              </div>
            </div>

            <p className="form-note">
              <ion-icon name="information-circle-outline" style={{ verticalAlign: '-2px', marginRight: '0.25rem' }} />
              {settings?.allowSharedSwitchByAll === false
                ? 'Only you can switch into this user (changeable in Settings).'
                : 'Any household member can switch into this user (changeable in Settings).'}
            </p>

            <div className="form-actions">
              <button type="submit" disabled={createSharedMember.isPending} className="btn btn-primary btn-full">
                {createSharedMember.isPending ? 'Adding…' : 'Add user'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowShareDevice(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete shared user confirmation */}
      {deleteTarget && (
        <Modal title="Remove shared user?" onClose={() => setDeleteTarget(null)}>
          <p className="modal-note">
            This removes <strong>{deleteTarget.memberName}</strong> from the household, along with
            their points and any chores still assigned to them. This can&apos;t be undone.
          </p>
          {deleteSharedMember.isError && (
            <div className="alert alert-error">Couldn&apos;t remove them just now. Please try again.</div>
          )}
          <div className="form-actions">
            <button
              className="btn btn-danger btn-full"
              onClick={() => void handleDeleteSharedMember()}
              disabled={deleteSharedMember.isPending}
            >
              {deleteSharedMember.isPending ? 'Removing…' : 'Remove'}
            </button>
            <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)} disabled={deleteSharedMember.isPending}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Invite Member Modal */}
      {showInvite && (
        <Modal title="Invite a Member" onClose={closeInvite}>
          {inviteToken ? (
            <>
              <p className="modal-note">
                Invitation created! Share this link with the invitee:
              </p>
              <div className="invite-box">
                <strong>Invitation link</strong>
                <code>
                  {window.location.origin}/invitations/{inviteToken}
                </code>
                <small>This link can only be used once.</small>
              </div>
              <div className="form-actions">
                <button className="btn btn-primary btn-full" onClick={() => void handleCopyLink()}>
                  <ion-icon name={linkCopied ? 'checkmark-outline' : 'copy-outline'} style={{ marginRight: '0.375rem' }} />
                  {linkCopied ? 'Copied!' : 'Copy link'}
                </button>
                {canShare && (
                  <button className="btn btn-ghost" onClick={() => void handleShareLink()}>
                    <ion-icon name="share-outline" />
                  </button>
                )}
                <button className="btn btn-ghost" onClick={closeInvite}>Done</button>
              </div>
            </>
          ) : (
            <form onSubmit={(e) => void handleInvite(e)}>
              <div className="field">
                <label htmlFor="invite-email">Email address</label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="member@example.com"
                  required
                  autoFocus
                />
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  disabled={inviteMember.isPending}
                  className="btn btn-primary btn-full"
                >
                  {inviteMember.isPending ? 'Sending…' : 'Send invite'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeInvite}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {/* Create Household Modal */}
      {showCreate && (
        <Modal title="Create a New Household" onClose={() => setShowCreate(false)}>
          <form onSubmit={(e) => void handleCreateHousehold(e)}>
            <div className="field">
              <label htmlFor="household-name">Household name</label>
              <input
                id="household-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Smith Family Home"
                required
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button
                type="submit"
                disabled={createHousehold.isPending}
                className="btn btn-primary btn-full"
              >
                {createHousehold.isPending ? 'Creating…' : 'Create household'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Member Profile Modal */}
      {showEditMember && (
        <Modal title="Your Member Profile" onClose={() => setShowEditMember(false)}>
          <form onSubmit={(e) => void handleSaveMember(e)}>
            <div className="field">
              <label htmlFor="member-name">Name</label>
              <input
                id="member-name"
                type="text"
                value={editMemberName}
                onChange={(e) => setEditMemberName(e.target.value)}
                placeholder="Your name in this household"
                maxLength={50}
                required
                autoFocus
              />
            </div>

            <div className="field">
              <label>Colour</label>
              <div className="swatch-row">
                {AVATAR_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`swatch${editAvatarColor === color ? ' swatch--active' : ''}`}
                    onClick={() => setEditAvatarColor(color)}
                    style={{ background: color }}
                    aria-label={color}
                    aria-pressed={editAvatarColor === color}
                  />
                ))}
              </div>
            </div>

            <div className="field">
              <label>Icon</label>
              <div className="swatch-row">
                {AVATAR_ICONS.map((icon) => {
                  const active = editAvatarIcon === icon;
                  return (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-swatch${active ? ' icon-swatch--active' : ''}`}
                      onClick={() => setEditAvatarIcon(icon)}
                      style={active ? { background: editAvatarColor, borderColor: editAvatarColor } : undefined}
                      aria-label={icon}
                      aria-pressed={active}
                    >
                      <ion-icon name={icon} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="member-preview">
              <div className="member-preview-id">
                <div className="avatar member-preview-avatar" style={{ background: editAvatarColor }}>
                  <ion-icon name={editAvatarIcon} />
                </div>
                <span className="member-preview-name">
                  {editMemberName || 'Your name'}
                </span>
              </div>
              <button type="submit" disabled={updateMember.isPending} className="btn btn-primary">
                {updateMember.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
